import React, { useState } from 'react';
import {
	Box,
	Container,
	Flex,
	Text,
	Image,
	Button,
	Input,
	Textarea,
	FormControl,
	FormLabel,
	VStack,
	HStack,
	Icon,
	SimpleGrid,
	Divider,
} from '@chakra-ui/react';
import { MdPhone, MdEmail, MdLocationOn, MdAccessTime } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import logoImage from '../../assets/logo.png';

const ContactPage = () => {
	const navigate = useNavigate();
	const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
	const [submitted, setSubmitted] = useState(false);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		// Placeholder: wire up to an email/form endpoint later
		setSubmitted(true);
	};

	return (
		<>
			{/* Navbar */}
			<Flex
				as="nav"
				align="center"
				justify="space-between"
				px={{ base: 4, md: 6 }}
				py={3}
				bg="linear-gradient(135deg, #0f3460 0%, #16213e 100%)"
				boxShadow="0 2px 20px rgba(0,0,0,0.3)"
				position="sticky"
				top="0"
				zIndex="999"
			>
				<Box
					cursor="pointer"
					onClick={() => navigate('/catalog')}
					bg="white"
					borderRadius="lg"
					px={3}
					py={1.5}
					_hover={{ opacity: 0.9 }}
				>
					<Image src={logoImage} alt="Logo" h={{ base: '24px', md: '36px' }} />
				</Box>
				<HStack gap={3}>
					<Button variant="ghost" color="whiteAlpha.800" size="sm" onClick={() => navigate('/catalog')}
						_hover={{ color: 'white', bg: 'whiteAlpha.200' }}>
						← Back to Catalog
					</Button>
					<Button colorScheme="green" size="sm" borderRadius="full" px={5}
						bg="green.400" _hover={{ bg: 'green.300' }} onClick={() => navigate('/login')}>
						Login
					</Button>
				</HStack>
			</Flex>

			{/* Hero */}
			<Box bg="linear-gradient(135deg, #1a4731 0%, #0f3460 100%)" py={16} px={4} textAlign="center">
				<Text fontSize={{ base: '3xl', md: '4xl' }} fontWeight="bold" color="white" mb={2}>
					Get in Touch
				</Text>
				<Text fontSize="lg" color="whiteAlpha.700" maxW="500px" mx="auto">
					Interested in ordering? Reach out and our team will get back to you shortly.
				</Text>
			</Box>

			<Container maxW="container.lg" py={12}>
				<SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>

					{/* Contact Info */}
					<VStack align="stretch" spacing={8}>
						<Box>
							<Text fontSize="2xl" fontWeight="bold" color="gray.800" mb={6}>Contact Information</Text>
							<VStack align="stretch" spacing={5}>
								<HStack spacing={4}>
									<Flex w={10} h={10} bg="green.50" borderRadius="xl" align="center" justify="center" flexShrink={0} boxShadow="sm">
										<Icon as={MdPhone} color="green.500" boxSize={5} />
									</Flex>
									<Box>
										<Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">Phone</Text>
										<Text fontWeight="medium" color="gray.800">(318)224-7121</Text>
									</Box>
								</HStack>

								<HStack spacing={4}>
									<Flex w={10} h={10} bg="green.50" borderRadius="xl" align="center" justify="center" flexShrink={0} boxShadow="sm">
										<Icon as={MdEmail} color="green.500" boxSize={5} />
									</Flex>
									<Box>
										<Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">Email</Text>
										<Text fontWeight="medium" color="gray.800">cs@correctcommissary.com</Text>
									</Box>
								</HStack>

								<HStack spacing={4}>
									<Flex w={10} h={10} bg="green.50" borderRadius="xl" align="center" justify="center" flexShrink={0} boxShadow="sm">
										<Icon as={MdLocationOn} color="green.500" boxSize={5} />
									</Flex>
									<Box>
										<Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">Address</Text>
										<Text fontWeight="medium" color="gray.800">1936 Farmerville Hwy</Text>
										<Text fontSize="sm" color="gray.500">Ruston, LA 71270</Text>
									</Box>
								</HStack>

								<HStack spacing={4}>
									<Flex w={10} h={10} bg="green.50" borderRadius="xl" align="center" justify="center" flexShrink={0} boxShadow="sm">
										<Icon as={MdAccessTime} color="green.500" boxSize={5} />
									</Flex>
									<Box>
										<Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">Hours</Text>
										<Text fontWeight="medium" color="gray.800">Mon – Fri: 8:00 AM – 5:00 PM</Text>
										<Text fontSize="sm" color="gray.500">Closed weekends & holidays</Text>
									</Box>
								</HStack>
							</VStack>
						</Box>

						<Divider />

						<Box bg="green.50" borderRadius="xl" p={5} border="1px solid" borderColor="green.100">
							<Text fontWeight="bold" color="green.700" mb={1}>Already a customer?</Text>
							<Text fontSize="sm" color="green.600" mb={3}>Log in to browse facility-specific pricing and place orders directly.</Text>
							<Button colorScheme="green" size="sm" borderRadius="full" onClick={() => navigate('/login')}>
								Login to Your Account
							</Button>
						</Box>
					</VStack>

					{/* Contact Form */}
					<Box bg="white" borderRadius="2xl" boxShadow="lg" border="1px solid" borderColor="gray.100" p={8}
						borderTop="4px solid" borderTopColor="green.400">
						{submitted ? (
							<VStack spacing={4} py={8} textAlign="center">
								<Box w={16} h={16} bg="green.50" borderRadius="full" display="flex" alignItems="center" justifyContent="center"
									border="2px solid" borderColor="green.200">
									<Text fontSize="3xl" color="green.500">✓</Text>
								</Box>
								<Text fontSize="xl" fontWeight="bold" color="gray.800">Message Sent!</Text>
								<Text color="gray.500" fontSize="sm">Thanks for reaching out. We'll get back to you within 1 business day.</Text>
								<Button variant="outline" colorScheme="green" size="sm" onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', message: '' }); }}>
									Send another message
								</Button>
							</VStack>
						) : (
							<form onSubmit={handleSubmit}>
								<VStack spacing={4}>
									<Text fontSize="xl" fontWeight="bold" color="gray.800" alignSelf="flex-start">Send us a message</Text>
									<FormControl isRequired>
										<FormLabel fontSize="sm" color="gray.600">Full Name</FormLabel>
										<Input name="name" value={form.name} onChange={handleChange} placeholder="John Smith" borderRadius="lg"
											focusBorderColor="green.400" />
									</FormControl>
									<FormControl isRequired>
										<FormLabel fontSize="sm" color="gray.600">Email</FormLabel>
										<Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="john@company.com" borderRadius="lg"
											focusBorderColor="green.400" />
									</FormControl>
									<FormControl>
										<FormLabel fontSize="sm" color="gray.600">Phone</FormLabel>
										<Input name="phone" value={form.phone} onChange={handleChange} placeholder="(318)224-7121" borderRadius="lg"
											focusBorderColor="green.400" />
									</FormControl>
									<FormControl isRequired>
										<FormLabel fontSize="sm" color="gray.600">Message</FormLabel>
										<Textarea name="message" value={form.message} onChange={handleChange}
											placeholder="Tell us what you're looking for..." borderRadius="lg" rows={5} resize="none"
											focusBorderColor="green.400" />
									</FormControl>
									<Button type="submit" colorScheme="green" w="full" borderRadius="xl" size="lg" fontWeight="bold"
										bg="green.400" _hover={{ bg: 'green.500' }}>
										Send Message
									</Button>
								</VStack>
							</form>
						)}
					</Box>
				</SimpleGrid>
			</Container>

			{/* Footer */}
			<Box as="footer" bg="gray.800" color="white" py={6} textAlign="center">
				<Text fontSize="sm" color="gray.400">© {new Date().getFullYear()} Correct Commissary. All rights reserved.</Text>
			</Box>
		</>
	);
};

export default ContactPage;
